import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Smartphone, Plus, Trash2, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const isValidUpi = (value) => {
  if (!value) return false;
  const trimmed = value.trim();
  if (trimmed.length < 3 || trimmed.length > 256) return false;
  return /^[a-zA-Z0-9._\-]+@[a-zA-Z][a-zA-Z0-9._\-]*$/.test(trimmed);
};

const AdminTopupUpiIdsSection = () => {
  const {
    topupUpiIds,
    topupPayeeName,
    refreshAdminTopupRequests,
    updateAdminTopupUpiIds,
  } = useAuth();

  const [draftIds, setDraftIds] = useState(['']);
  const [payeeName, setPayeeName] = useState('Harday');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Hydrate from server on first mount
  useEffect(() => {
    refreshAdminTopupRequests({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync draft when context value updates from server
  useEffect(() => {
    if (Array.isArray(topupUpiIds) && topupUpiIds.length > 0) {
      setDraftIds(topupUpiIds);
    }
  }, [topupUpiIds]);

  useEffect(() => {
    if (topupPayeeName) setPayeeName(topupPayeeName);
  }, [topupPayeeName]);

  const handleChange = (index, value) => {
    setMessage('');
    setError('');
    setDraftIds((prev) => prev.map((item, i) => (i === index ? value : item)));
  };

  const handleRemove = (index) => {
    setMessage('');
    setError('');
    setDraftIds((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    setMessage('');
    setError('');
    setDraftIds((prev) => [...prev, '']);
  };

  const cleaned = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const raw of draftIds) {
      const trimmed = (raw || '').trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(trimmed);
    }
    return out;
  }, [draftIds]);

  const allValid = cleaned.length > 0 && cleaned.every((id) => isValidUpi(id));

  const handleSave = async () => {
    setError('');
    setMessage('');
    if (!allValid) {
      setError('Add at least one valid UPI ID (e.g. name@bank).');
      return;
    }
    setSaving(true);
    const result = await updateAdminTopupUpiIds(cleaned, payeeName.trim() || 'Harday');
    setSaving(false);
    if (!result.success) {
      setError(result.error || 'Failed to save UPI IDs');
    } else {
      setMessage('UPI IDs saved. New requests will rotate across these IDs.');
    }
  };

  return (
    <motion.div
      className="glass-panel rounded-[18px] overflow-hidden"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.12 }}
      data-testid="admin-topup-upi-ids-section"
    >
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left"
        aria-expanded={isOpen}
        aria-controls="admin-topup-upi-ids-content"
        data-testid="admin-topup-upi-ids-toggle"
      >
        <span className="flex items-center gap-2 text-[#666666] text-sm font-['DM_Sans']">
          <Smartphone className="w-4 h-4 text-[#9333EA]" />
          Top-Up UPI IDs (rotate across requests)
        </span>
        <ChevronDown
          className={`w-5 h-5 text-[#666666] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <motion.div
          id="admin-topup-upi-ids-content"
          className="border-t border-[#ececec] px-4 pb-4 pt-3 space-y-3"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
        >
          <div>
            <label className="block text-xs text-[#666666] mb-1 font-['DM_Sans']">
              Payee name (shown in UPI app)
            </label>
            <input
              type="text"
              value={payeeName}
              onChange={(e) => {
                setPayeeName(e.target.value);
                setMessage('');
                setError('');
              }}
              placeholder="Harday"
              className="w-full p-2.5 rounded-[12px] border border-[#e9e9e9] bg-white text-sm"
              data-testid="admin-topup-payee-input"
            />
          </div>

          <div className="space-y-2">
            {draftIds.map((id, index) => {
              const trimmed = (id || '').trim();
              const showInvalid = trimmed.length > 0 && !isValidUpi(trimmed);
              return (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={id}
                    onChange={(e) => handleChange(index, e.target.value)}
                    placeholder="yourname@bank"
                    className={`flex-1 p-2.5 rounded-[12px] border bg-white text-sm ${
                      showInvalid ? 'border-red-300' : 'border-[#e9e9e9]'
                    }`}
                    data-testid={`admin-topup-upi-input-${index}`}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    disabled={draftIds.length <= 1}
                    className="h-9 w-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center disabled:opacity-40"
                    title="Remove"
                    data-testid={`admin-topup-upi-remove-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={handleAdd}
            className="w-full flex items-center justify-center gap-1.5 rounded-full border border-dashed border-[#9333EA]/40 text-[#9333EA] text-xs font-semibold py-2"
            data-testid="admin-topup-upi-add-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Add another UPI ID
          </button>

          <p className="text-[11px] text-[#666666] font-['DM_Sans']">
            Requests are evenly distributed across these UPI IDs (round-robin). Adding more IDs
            immediately spreads the load.
          </p>

          {error && (
            <p className="rounded-[10px] bg-red-50 border border-red-100 text-red-700 px-2 py-1.5 text-[11px] font-['DM_Sans']">
              {error}
            </p>
          )}
          {message && (
            <p className="rounded-[10px] bg-emerald-50 border border-emerald-100 text-emerald-700 px-2 py-1.5 text-[11px] font-['DM_Sans']">
              {message}
            </p>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !allValid}
            className="w-full py-2.5 rounded-full bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="admin-topup-upi-save-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save UPI IDs'}
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default AdminTopupUpiIdsSection;
